import {
  type CallingCountryCode,
  type CallingCountryCodeAlpha3,
} from '@wix/form-validator';
import { Globe } from '@wix/wix-ui-icons-common';
import React, {
  type ReactNode,
  useRef,
  createContext,
  useContext,
  useMemo,
} from 'react';
import {
  Select as SelectRAC,
  Button as ButtonRAC,
  Popover as PopoverRAC,
  ListBox as ListBoxRAC,
  ListBoxItem as ListBoxItemRAC,
  Input as InputRAC,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { useTranslation } from '../../contexts/translation-context';
import { findChildOfType, applyInternalProps } from '../../services/find-child';
import { Description } from '../field-description/field-description-headless';
import { Error as FieldError } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';

import { getCallingCode, type Country } from './phone-input/phone-input-utils';
import { filterCountries, useCountries } from './phone-input/use-countries';
import { usePhoneInputLogic } from './phone-input/use-phone-input-logic';
import { type PhoneInputFieldProps } from './phone-input-field-props';

type PhoneInputContextValue = {
  currentValue: string;
  selectedCountryCode: CallingCountryCode | undefined;
  handlePhoneChange: (value: string) => void;
  handleCountryCodeChange: (countryCode: CallingCountryCode) => void;
  countryCodes: CallingCountryCode[];
  countries: Country[];
};

const PhoneInputContext = createContext<PhoneInputContextValue | null>(null);

const usePhoneInputContext = (): PhoneInputContextValue => {
  const context = useContext(PhoneInputContext);
  if (!context) {
    throw new Error(
      'Phone input components must be used within PhoneInput component',
    );
  }
  return context;
};

type OptionDataContextValue = {
  countryCode: CallingCountryCode;
  dialingCode: string;
  countryName: string;
  longCountryCode: CallingCountryCodeAlpha3;
};

const OptionDataContext = createContext<OptionDataContextValue | null>(null);

const useOptionDataContext = (): OptionDataContextValue => {
  const context = useContext(OptionDataContext);
  if (!context) {
    throw new Error(
      'Option sub-components must be used within Option component',
    );
  }
  return context;
};

const Flag = ({
  longCountryCode,
  className,
}: {
  longCountryCode: CallingCountryCodeAlpha3;
  className?: string;
}) => {
  function getFlagUrl(
    _longCountryCode: CallingCountryCodeAlpha3,
    size: '1x' | '2x',
  ) {
    const baseUrl =
      'https://static.parastorage.com/services/linguist-flags/1.1005.0/assets/flags/square/';

    const suffix = size === '2x' ? '_2x' : '';

    const file = `${_longCountryCode}${suffix}.png`;

    return `${baseUrl}${file}`;
  }

  const flag1x = getFlagUrl(longCountryCode, '1x');
  const flag2x = getFlagUrl(longCountryCode, '2x');

  return (
    <img
      src={flag1x}
      srcSet={`${flag1x} 1x, ${flag2x} 2x`}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className={className}
      style={{ width: '22px', height: 'auto' }}
    />
  );
};

export type PhoneInputProps = {
  children: ReactNode;
};

export const PhoneInput = ({ children }: PhoneInputProps) => {
  const { id, value, onChange, FieldLayout, defaultCountry, validation } =
    useFieldPropsV2<PhoneInputFieldProps>();

  const allowedCountryCodes = validation?.string?.phoneOptions
    ?.allowedCountryCodes as CallingCountryCode[] | undefined;

  const allCountries = useCountries();
  const countries = useMemo(
    () => filterCountries(allCountries, allowedCountryCodes),
    [allCountries, allowedCountryCodes],
  );

  const countryCodes = useMemo(
    () => countries.map((country) => country.shortCountryCode),
    [countries],
  );

  const {
    currentValue,
    selectedCountryCode,
    handlePhoneChange,
    handleCountryCodeChange,
  } = usePhoneInputLogic({
    value,
    defaultCountryCode: defaultCountry,
    allowedCountryCodes: countryCodes,
    onChange,
  });

  const labelElement = findChildOfType(children, PhoneInput.Label);
  const controlElement = findChildOfType(children, PhoneInput.Control);
  const descriptionElement = findChildOfType(children, PhoneInput.Description);
  const errorElement = findChildOfType(children, PhoneInput.Error);

  const contextValue: PhoneInputContextValue = {
    currentValue,
    selectedCountryCode,
    handlePhoneChange,
    handleCountryCodeChange,
    countryCodes,
    countries,
  };

  return (
    <PhoneInputContext.Provider value={contextValue}>
      <FieldLayout
        fieldId={id}
        renderLabel={() => labelElement}
        renderInput={() => controlElement}
        renderDescription={() => (
          <>
            {descriptionElement}
            {errorElement}
          </>
        )}
      />
    </PhoneInputContext.Provider>
  );
};

type CountryButtonProps = {
  children?: ReactNode;
  className?: string;
};

const CountryButton = ({ children, className }: CountryButtonProps) => {
  const { inputId, labelId } = useFieldAttributes();
  const { id, hasError } = useFieldPropsV2<PhoneInputFieldProps>();
  const { t } = useTranslation();
  const { selectedCountryCode, countries } = usePhoneInputContext();
  const buttonId = `${inputId}-country`;

  const ref = useRef<HTMLButtonElement>(null);
  useFocusFieldEvent(() => {
    ref.current?.focus();
  }, id);

  const selectedCountry = selectedCountryCode
    ? countries.find((c) => c.shortCountryCode === selectedCountryCode)
    : undefined;
  const dialingCode = selectedCountry
    ? getCallingCode(selectedCountry.longCountryCode) || ''
    : '';
  const countryCodeLabel = t('field.phone.country-code.aria-label');
  const ariaLabel = selectedCountry
    ? `${countryCodeLabel}, ${selectedCountry.countryName}, ${dialingCode}`
    : countryCodeLabel;

  return (
    <ButtonRAC
      ref={ref}
      id={buttonId}
      aria-label={ariaLabel}
      aria-labelledby={labelId ? `${labelId} ${buttonId}` : undefined}
      className={className}
      data-invalid={hasError}
    >
      {children}
    </ButtonRAC>
  );
};

type CountryButtonFlagProps = {
  className?: string;
};

const CountryButtonFlag = ({ className }: CountryButtonFlagProps) => {
  const { selectedCountryCode, countries } = usePhoneInputContext();
  const selectedCountry = countries.find(
    (c) => c.shortCountryCode === selectedCountryCode,
  );

  if (!selectedCountry) {
    return null;
  }

  return (
    <Flag
      className={className}
      longCountryCode={selectedCountry.longCountryCode}
    />
  );
};

type CountryButtonEmptyStateProps = {
  className?: string;
};

const CountryButtonEmptyState = ({
  className,
}: CountryButtonEmptyStateProps) => {
  const { selectedCountryCode } = usePhoneInputContext();

  if (selectedCountryCode) {
    return null;
  }

  return <Globe className={className} width={22} height={22} />;
};

CountryButton.Flag = CountryButtonFlag;
CountryButton.EmptyState = CountryButtonEmptyState;

type CountryOptionsProps = {
  children: ReactNode;
  className?: string;
};

const CountryOptions = ({ children, className }: CountryOptionsProps) => {
  const { countries } = usePhoneInputContext();
  const optionElement = findChildOfType(children, CountryOptions.Option);

  return (
    <PopoverRAC className={className}>
      <ListBoxRAC>
        {countries.map((country) => {
          const dialingCode = getCallingCode(country.longCountryCode) || '';

          return applyInternalProps<OptionInternalProps>(optionElement, {
            key: country.shortCountryCode,
            countryCode: country.shortCountryCode,
            dialingCode,
            countryName: country.countryName,
            longCountryCode: country.longCountryCode,
          });
        })}
      </ListBoxRAC>
    </PopoverRAC>
  );
};

type OptionProps = {
  children?: ReactNode;
  className?: string;
};

type OptionInternalProps = {
  countryCode: CallingCountryCode;
  dialingCode: string;
  countryName: string;
  longCountryCode: CallingCountryCodeAlpha3;
};

const Option = ({ children, className, ...internal }: OptionProps) => {
  const { countryCode, dialingCode, countryName, longCountryCode } =
    internal as OptionInternalProps;

  const optionData: OptionDataContextValue = {
    countryCode,
    dialingCode,
    countryName,
    longCountryCode,
  };

  const content = children || (
    <>
      <OptionFlag />
      <OptionName />
      <OptionCode />
    </>
  );

  return (
    <ListBoxItemRAC
      className={className}
      id={countryCode}
      textValue={`${countryName} ${dialingCode}`}
    >
      <OptionDataContext.Provider value={optionData}>
        {content}
      </OptionDataContext.Provider>
    </ListBoxItemRAC>
  );
};

type OptionFlagProps = {
  className?: string;
};

const OptionFlag = ({ className }: OptionFlagProps) => {
  const { longCountryCode } = useOptionDataContext();
  return <Flag className={className} longCountryCode={longCountryCode} />;
};

type OptionNameProps = {
  className?: string;
};

const OptionName = ({ className }: OptionNameProps) => {
  const { countryName } = useOptionDataContext();
  return <span className={className}>{countryName}</span>;
};

type OptionCodeProps = {
  className?: string;
};

const OptionCode = ({ className }: OptionCodeProps) => {
  const { dialingCode } = useOptionDataContext();
  return <span className={className}>{dialingCode}</span>;
};

Option.Flag = OptionFlag;
Option.Name = OptionName;
Option.Code = OptionCode;

CountryOptions.Option = Option;

type PhoneNumberProps = {
  className?: string;
};

const PhoneNumber = ({ className }: PhoneNumberProps) => {
  const { t } = useTranslation();
  const { inputId, ariaDescribedBy } = useFieldAttributes();
  const {
    id,
    placeholder,
    disabled,
    required,
    onBlur,
    onFocus,
    label,
    hasError,
  } = useFieldPropsV2<PhoneInputFieldProps>();
  const { currentValue, handlePhoneChange } = usePhoneInputContext();

  const ref = useRef<HTMLInputElement>(null);
  useFocusFieldEvent(() => {
    ref.current?.focus();
  }, id);

  return (
    <InputRAC
      ref={ref}
      id={inputId}
      type="tel"
      value={currentValue}
      onChange={(e) => handlePhoneChange(e.target.value)}
      disabled={disabled}
      required={required}
      onBlur={onBlur}
      onFocus={onFocus}
      aria-label={`${label}. ${t('field.phone.aria-label')}`}
      aria-describedby={ariaDescribedBy}
      aria-invalid={hasError}
      placeholder={placeholder}
      className={className}
    />
  );
};

type ControlProps = {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

const Control = ({ children, className }: ControlProps) => {
  const { disabled, required, hasError } =
    useFieldPropsV2<PhoneInputFieldProps>();
  const { selectedCountryCode, handleCountryCodeChange } =
    usePhoneInputContext();

  const countryButtonElement = findChildOfType(
    children,
    PhoneInput.CountryButton,
  );
  const countryOptionsElement = findChildOfType(
    children,
    PhoneInput.CountryOptions,
  );
  const phoneNumberElement = findChildOfType(children, PhoneInput.PhoneNumber);

  return (
    <div className={className}>
      <SelectRAC
        value={selectedCountryCode ?? null}
        onChange={(key) => handleCountryCodeChange(key as CallingCountryCode)}
        isDisabled={disabled}
        isInvalid={hasError}
        isRequired={required}
        placeholder=""
      >
        {countryButtonElement}
        {countryOptionsElement}
      </SelectRAC>
      {phoneNumberElement}
    </div>
  );
};

PhoneInput.Label = Label;
PhoneInput.Control = Control;
PhoneInput.CountryButton = CountryButton;
PhoneInput.CountryOptions = CountryOptions;
PhoneInput.PhoneNumber = PhoneNumber;
PhoneInput.Description = Description;
PhoneInput.Error = FieldError;
